import React, { useState, useEffect } from 'react';
import ExistingAudiences from '../existingAudience/ExistingAudiences';
import UnifiedSegmentsSelector from '../../components/segments/UnifiedSegmentsSelector';
import ChatUI from './ChatUI';
import { useAudience } from '@/context/AudienceContext';
import Layout from '../../components/Layout';
import ProgressSteps from '../../components/ProgressSteps';

const ChatWithPersona: React.FC = () => {
  // All hooks at the top
  const [step, setStep] = useState<'audience' | 'segments' | 'chat'>('audience');
  const [selectedAudienceId, setSelectedAudienceId] = useState<number | null>(null);
  const [selectedAudienceName, setSelectedAudienceName] = useState<string>('');
  const [chatPersonaIds, setChatPersonaIds] = useState<number[]>([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState<number[]>([]);
  const [personaFilters, setPersonaFilters] = useState<Record<number, any>>({});
  const { currentStep, setCurrentStep, audienceData, updateAudienceData } = useAudience();

  // Always call useEffect
  useEffect(() => {
    const fetchPersonaIds = async () => {
      if (step !== 'chat') return;
      setLoadingPersonas(true);
      try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const filterRequest = {
          segments: audienceData.selectedSegments,
          filters: audienceData.personaFilters,
          audience_name: audienceData.audienceName || ''
        };
        const response = await fetch(`${API_URL}/filter_personas`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(filterRequest)
        });
        if (!response.ok) throw new Error('Failed to fetch persona IDs');
        const roleToPersonasMap = await response.json();
        // Flatten all persona IDs from all roles
        // Backend returns persona objects with {id, name, ...}, not just IDs
        const allPersonaIds: number[] = [];
        Object.values(roleToPersonasMap).forEach((personas: any) => {
          if (Array.isArray(personas)) {
            personas.forEach((persona: any) => {
              // Handle both persona objects (with id field) and raw IDs
              const id = typeof persona === 'object' ? persona.id : persona;
              if (typeof id === 'number') {
                allPersonaIds.push(id);
              }
            });
          }
        });
        setChatPersonaIds(Array.from(new Set(allPersonaIds)));
      } catch (error) {
        console.error('Error fetching persona IDs:', error);
        setChatPersonaIds([]);
      } finally {
        setLoadingPersonas(false);
      }
    };
    fetchPersonaIds();
  }, [step, audienceData.selectedSegments, audienceData.personaFilters, audienceData.audienceName]);

  // Handler for audience selection
  const handleSelectAudience = (audienceId: number, audienceName: string) => {
    setSelectedAudienceId(audienceId);
    setSelectedAudienceName(audienceName);
    updateAudienceData({ 
      audienceId: audienceId,
      audienceName: audienceName,
      selectedSegments: [],
      personaFilters: {}
    });
    setStep('segments');
  };

  // Handler for segment selection
  const handleSegmentsNext = (segments: number[], filters: Record<number, any>) => {
    setSelectedSegments(segments);
    setPersonaFilters(filters);
    updateAudienceData({
      selectedSegments: segments,
      personaFilters: filters
    });
    setStep('chat');
  };

  // Generate progress steps for chat flow
  const getProgressSteps = () => {
    return [
      {
        label: selectedAudienceName ? `1. Audience: ${selectedAudienceName}` : '1. Select Audience',
        completed: step === 'segments' || step === 'chat',
        current: step === 'audience',
        onClick: () => setStep('audience')
      },
      {
        label: '2. Select Segments',
        completed: step === 'chat',
        current: step === 'segments',
        onClick: selectedAudienceId ? () => setStep('segments') : undefined
      },
      {
        label: '3. Chat with Personas',
        completed: false,
        current: step === 'chat',
        onClick: (step === 'chat') ? () => setStep('chat') : undefined
      }
    ];
  };

  // Conditional rendering only
  if (step === 'audience') {
    return (
      <Layout>
        <div className="mb-8">
          <ProgressSteps steps={getProgressSteps()} />
        </div>
        <ExistingAudiences
          onSelectAudience={handleSelectAudience}
          onBack={() => {}}
        />
      </Layout>
    );
  }

  if (step === 'segments' && selectedAudienceId) {
    return (
      <Layout>
        <div className="mb-8">
          <ProgressSteps steps={getProgressSteps()} />
        </div>
        <UnifiedSegmentsSelector
          btnText="Continue to Chat"
          audienceId={selectedAudienceId}
          onBack={() => setStep('audience')}
          onNext={handleSegmentsNext}
        />
      </Layout>
    );
  }

  if (step === 'chat') {
    if (loadingPersonas) {
      return (
        <Layout>
          <div className="mb-8">
            <ProgressSteps steps={getProgressSteps()} />
          </div>
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-500">
            <svg className="animate-spin h-10 w-10 mb-4 text-blue-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <div className="text-lg font-medium">Loading personas for chat...</div>
          </div>
        </Layout>
      );
    }
    if (chatPersonaIds.length === 0) {
      return (
        <Layout>
          <div className="mb-8">
            <ProgressSteps steps={getProgressSteps()} />
          </div>
          <div>No personas found for the selected segments and filters.</div>
        </Layout>
      );
    }
    return (
      <Layout>
        <div className="mb-8">
          <ProgressSteps steps={getProgressSteps()} />
        </div>
        <div className="flex flex-col min-h-[80vh] items-center justify-center py-10 px-2">
          <div className="w-full max-w-6xl mx-auto mt-8">
            <div className="mb-6 flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-gray-800">Chat with Personas</h1>
              <button
                onClick={() => setStep('segments')}
                className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 shadow-sm"
              >
                Back to segment selection
              </button>
            </div>
            <ChatUI personaIds={chatPersonaIds} />
          </div>
        </div>
      </Layout>
    );
  }

  return null;
};

export default ChatWithPersona;
